import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import apiService from '../../services/ApiServices';
import { io } from "socket.io-client";
import {
  FaHome, FaCalendarAlt, FaUsers, FaClock, FaUserCheck,
  FaPuzzlePiece, FaChartBar, FaQuestionCircle, FaChevronRight,
  FaBars, FaBell, FaSearch, FaCalendarCheck, FaUser, FaCog,
  FaSignOutAlt, FaCrown, FaCogs, FaShieldAlt, FaCreditCard,
  FaMobileAlt, FaEnvelope, FaFileAlt, FaTimes
} from 'react-icons/fa';
import './NavBar.css';

// Mapping: backend icon name → React Icon component
const iconMap = {
  Home: FaHome,
  Dashboard: FaHome,
  Events: FaCalendarAlt,
  Meetings: FaUsers,
  Availability: FaClock,
  Profile: FaUserCheck,
  Integrations: FaPuzzlePiece,
  Analytics: FaChartBar,
  Settings: FaCog,
  'Help Center': FaQuestionCircle,
  Help: FaQuestionCircle,
  Upgrade: FaCrown,
  Admin: FaCogs,
  Security: FaShieldAlt,
  Billing: FaCreditCard,
  Mobile: FaMobileAlt,
  Messages: FaEnvelope,
  Documents: FaFileAlt,
  Calendar: FaCalendarCheck,
  Users: FaUsers,
  // Add more as needed
  // Default fallback
  default: FaHome
};

const normalizeUserRecord = (user = {}) => ({
  ...user,
  user_id: Number(user.user_id ?? user.userId ?? 0) || null,
  college_id: user.college_id ?? user.collegeId ?? null,
  name: user.name || user.user_name || user.username || "",
});

const normalizeNotificationRecord = (note = {}) => ({
  ...note,
  notification_id: note.notification_id || note.id || note.chat_id || note.related_id || null,
  chat_id: note.chat_id || note.related_id || note.id || null,
  chat_type: note.chat_type || note.chatType || note.notification_type || note.type || "individual",
  message: note.message || note.body || note.text || "New message",
  created_at: note.created_at || note.createdAt || note.sent_at || new Date().toISOString(),
  is_read: Boolean(note.is_read ?? note.isRead ?? false),
  sender_user: normalizeUserRecord(note.sender_user || note.senderUser || {}),
  receiver_user: normalizeUserRecord(note.receiver_user || note.receiverUser || {}),
  sender_name: note.sender_name || note.senderUser?.name || note.sender_user?.name || note.name || "",
  receiver_name: note.receiver_name || note.receiverUser?.name || note.receiver_user?.name || "",
  sender_id: Number(note.sender_id ?? note.user_id ?? note.sender_user?.user_id ?? note.senderUser?.user_id ?? 0) || null,
  receiver_id: Number(note.receiver_id ?? note.target_user_id ?? note.receiver_user?.user_id ?? note.receiverUser?.user_id ?? 0) || null,
  student_id: Number(note.student_id ?? note.studentId ?? note.sender_user?.student_id ?? note.receiver_user?.student_id ?? 0) || null,
  staff_id: Number(note.staff_id ?? note.staffId ?? note.sender_user?.staff_id ?? note.receiver_user?.staff_id ?? 0) || null,
  college_id: note.college_id ?? note.collegeId ?? note.sender_user?.college_id ?? note.receiver_user?.college_id ?? null,
});

const notificationIdentity = (note = {}) =>
  [
    note.chat_id || note.related_id || note.notification_id || "",
    Number(note.sender_id || note.sender_user?.user_id || 0),
    Number(note.receiver_id || note.receiver_user?.user_id || 0),
    note.chat_type || "individual",
    note.message || "",
  ].join("|");

const NavBar = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [menus, setMenus] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(true);
  const [user, setUser] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [notificationTab, setNotificationTab] = useState("group");
  const [notificationsByType, setNotificationsByType] = useState({
    group: [],
    individual: [],
  });
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const socketRef = useRef(null);
  const seenNotificationKeysRef = useRef(new Set());

  useEffect(() => {
    // Get user from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
      } catch (err) {
        console.error('Failed to parse user from localStorage:', err);
      }
    }

    // Fetch menus
    const fetchMenus = async () => {
      try {
        setLoadingMenus(true);
        const response = await apiService.get('/api/menus');

        if (response?.success && Array.isArray(response?.message?.menus)) {
          setMenus(response.message.menus);
        } else {
          console.warn('Invalid menu response format');
        }
      } catch (error) {
        console.error('Failed to load menus:', error);
      } finally {
        setLoadingMenus(false);
      }
    };

    fetchMenus();

    // Close sidebar when route changes
    setSidebarOpen(false);
  }, [location]);

  const fetchNotificationsByType = useCallback(async (userId, chatType) => {
    try {
      setNotificationsLoading(true);
      const response = await apiService.get('/api/notifications', {
        receiverId: userId,
        receiver_id: userId,
        user_id: userId,
        chatType,
        chat_type: chatType,
      });
      if (response?.success && Array.isArray(response?.message?.notifications)) {
        const filteredNotifications = [];
        const seenKeys = new Set();
        response.message.notifications
          .map(normalizeNotificationRecord)
          .forEach((note) => {
            if (Number(note?.sender_id || note?.sender_user?.user_id || 0) === Number(userId)) {
              return;
            }
            const key = notificationIdentity(note);
            if (seenKeys.has(key)) return;
            seenKeys.add(key);
            seenNotificationKeysRef.current.add(key);
            filteredNotifications.push(note);
          });
        setNotificationsByType((prev) => ({
          ...prev,
          [chatType]: filteredNotifications,
        }));
      } else {
        setNotificationsByType((prev) => ({
          ...prev,
          [chatType]: [],
        }));
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  const refreshNotifications = useCallback(async (userId) => {
    if (!userId) return;
    await Promise.all([
      fetchNotificationsByType(userId, "group"),
      fetchNotificationsByType(userId, "individual"),
    ]).catch(() => {});
  }, [fetchNotificationsByType]);

  useEffect(() => {
    if (user?.user_id) {
      refreshNotifications(user.user_id).catch(() => {});
    }
  }, [user?.user_id, refreshNotifications]);

  useEffect(() => {
    if (!user?.user_id) {
      return;
    }
    const socketUrl =
      process.env.REACT_APP_SOCKET_URL || "https://api.japaking.online";
    const socket = io(socketUrl, {
      transports: ["websocket"],
      withCredentials: true,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("join", { user_id: user.user_id, userId: user.user_id, id: user.user_id });
    });

    socket.on("connect_error", (error) => {
      console.error("Notification socket connect_error:", error?.message || error);
    });

    socket.on("disconnect", (reason) => {
      console.warn("Notification socket disconnected:", reason);
    });

    const pushNotification = (payload) => {
      const notification = normalizeNotificationRecord(payload);
      if (
        Number(notification.sender_id || notification.sender_user?.user_id || 0) ===
        Number(user.user_id)
      ) {
        return;
      }
      const type = notification.chat_type === "group" ? "group" : "individual";
      const key = notificationIdentity(notification);
      if (seenNotificationKeysRef.current.has(key)) return;
      seenNotificationKeysRef.current.add(key);
      setNotificationsByType((prev) => ({
        ...prev,
        [type]: [notification, ...(prev[type] || [])],
      }));
    };

    ["notification:new", "notifications:new", "notification:created"].forEach((eventName) => {
      socket.on(eventName, pushNotification);
    });

    return () => {
      socket.disconnect();
    };
  }, [user?.user_id, refreshNotifications]);

  const handleNotificationToggle = () => {
    setNotificationDropdownOpen((prev) => !prev);
  };

  const handleNotificationTabChange = (tab) => {
    setNotificationTab(tab);
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await apiService.patch(`/api/notifications/${notificationId}/read`);
      setNotificationsByType((prev) => {
        const markRead = (items) =>
          items.map((note) =>
            note.notification_id === notificationId
              ? { ...note, is_read: true }
              : note
          );
        return {
          group: markRead(prev.group),
          individual: markRead(prev.individual),
        };
      });
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    const unread = [
      ...notificationsByType.group,
      ...notificationsByType.individual,
    ].filter((note) => !note.is_read);
    if (!unread.length) {
      return;
    }
    try {
      await Promise.all(
        unread.map((note) =>
          apiService.patch(`/api/notifications/${note.notification_id}/read`)
        )
      );
      setNotificationsByType((prev) => ({
        group: prev.group.map((note) => ({ ...note, is_read: true })),
        individual: prev.individual.map((note) => ({ ...note, is_read: true })),
      }));
    } catch (error) {
      console.error('Failed to mark all notifications read:', error);
    }
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('AUTH_TOKEN_KEY');
    setUser(null);
    setUserDropdownOpen(false);
    navigate('/login');
  };

  const getUserInitial = () => user?.name?.charAt(0)?.toUpperCase() || 'U';
  const getUserName = () => user?.name || 'User';
  const getRoleName = () => user?.role_name || 'User';
  const unreadGroupCount = useMemo(
    () => notificationsByType.group.filter((note) => !note.is_read).length,
    [notificationsByType.group]
  );
  const unreadIndividualCount = useMemo(
    () =>
      notificationsByType.individual.filter((note) => !note.is_read).length,
    [notificationsByType.individual]
  );
  const unreadCount = unreadGroupCount + unreadIndividualCount;
  const activeNotifications = notificationsByType[notificationTab] || [];

  const getCollegeName = (user) =>
    user?.college?.college_name ||
    user?.college?.name ||
    user?.college_name ||
    user?.college ||
    "";

  const getSenderSummary = (note) => {
    const normalized = normalizeNotificationRecord(note);
    const sender = normalized.sender_user || {};
    const senderRoleName = sender?.role?.role_name || "User";
    const senderRole = senderRoleName.toLowerCase();
    const schoolName = getCollegeName(sender);
    const adminCollegeName = schoolName || "null";
    const phoneNumber = sender?.phone_number || "";
    const isStudent =
      senderRole.includes("student") || sender?.role_id === 1;
    const senderName = sender?.name || normalized.sender_name || (normalized.sender_id ? `User ${normalized.sender_id}` : "User");

    if (isStudent) {
      return [
        `Sent by: ${senderName}`,
        `Role: ${senderRoleName}`,
        `School: ${schoolName || "null"}`,
        phoneNumber ? `Phone: ${phoneNumber}` : null,
      ].filter(Boolean);
    }

    return [
      `Sent by: ${senderName}`,
      `Role: ${senderRoleName}`,
      `College: ${adminCollegeName}`,
    ];
  };

  const buildNotificationLink = (note) => {
    const normalized = normalizeNotificationRecord(note);
    const sender = normalized.sender_user || {};
    const receiver = normalized.receiver_user || {};
    const chatType = normalized.chat_type || "individual";
    const senderRole = (sender?.role?.role_name || "").toLowerCase();
    const receiverRole = (receiver?.role?.role_name || "").toLowerCase();
    const senderIsStudent = senderRole.includes("student") || sender?.role_id === 1;
    const receiverIsStudent =
      receiverRole.includes("student") || receiver?.role_id === 1;
    const studentUser =
      senderIsStudent
        ? sender
        : receiverIsStudent
          ? receiver
          : normalized.student_id
            ? { user_id: normalized.student_id, college_id: normalized.college_id || sender?.college_id || receiver?.college_id || null }
            : null;
    const staffUser =
      !senderIsStudent
        ? sender
        : !receiverIsStudent
          ? receiver
          : normalized.staff_id
            ? { user_id: normalized.staff_id, college_id: normalized.college_id || sender?.college_id || receiver?.college_id || null }
            : sender;

    if (!studentUser) {
      return "/admin-chat";
    }

    if (chatType === "group") {
      const params = new URLSearchParams({
        chatType: "group",
        studentId: String(studentUser?.user_id || ""),
        collegeId: String(studentUser?.college_id || normalized.college_id || ""),
      });
      return `/admin-chat?${params.toString()}`;
    }

    const params = new URLSearchParams({
      chatType: "individual",
      studentId: String(studentUser?.user_id || ""),
      staffId: String(staffUser?.user_id || ""),
      collegeId: String(studentUser?.college_id || normalized.college_id || ""),
    });
    return `/admin-chat?${params.toString()}`;
  };

  const handleNotificationOpen = async (note) => {
    try {
      if (!note?.is_read) {
        await handleMarkRead(note.notification_id);
      }
    } finally {
      setNotificationDropdownOpen(false);
      navigate(buildNotificationLink(note));
    }
  };

  const isActivePath = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  // Filter menus user has permission to view
  const visibleMenus = menus.filter(menu => {
    if (!user?.role_id) return false;
    return menu.role_mappings?.some(
      mapping => mapping.role_id === user.role_id && mapping.can_view === true
    );
  }).sort((a, b) => (a.display_order || 999) - (b.display_order || 999));

  return (
    <>
      {/* Top Navigation Bar */}
      <nav className="navbar navbar-expand-lg navbar-main fixed-top">
        <div className="container-fluid p-0">
          <div className="navbar-container">
            {/* Left - Toggle + Brand */}
            <div className="navbar-left">
              <button className="action-btn" type="button" onClick={toggleSidebar}>
                <FaBars size={16} />
              </button>

              <div className="brand-section">
                <Link className="navbar-brand-custom" to="/" onClick={closeSidebar}>
                  <div className="brand-logo">
                    <FaCalendarCheck size={18} />
                  </div>
                  <span>JapaKing</span>
                </Link>
              </div>
            </div>

            {/* Right - Actions */}
            <div className="navbar-right">
              {/* Notifications */}
              <div className="dropdown me-2">
                <button
                  className="action-btn position-relative"
                  onClick={handleNotificationToggle}
                >
                  <FaBell size={16} />
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount}</span>
                  )}
                </button>

                {notificationDropdownOpen && (
                  <div
                    className="dropdown-menu dropdown-menu-end show shadow border-0 navbar-notifications"
                    style={{
                      minWidth: '300px',
                      right: 0,
                      left: 'auto',
                      transform: 'translateX(0)',
                      marginTop: '15px'
                    }}
                  >
                    <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                      <h6 className="mb-0 fw-bold">Notifications</h6>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleMarkAllRead}
                      >
                        Mark all read
                      </button>
                    </div>
                    <div className="notification-tabs">
                      <button
                        className={`notification-tab ${
                          notificationTab === "group" ? "active" : ""
                        }`}
                        onClick={() => handleNotificationTabChange("group")}
                      >
                        Group
                        <span className="notification-tab__count">
                          {unreadGroupCount}
                        </span>
                      </button>
                      <button
                        className={`notification-tab ${
                          notificationTab === "individual" ? "active" : ""
                        }`}
                        onClick={() => handleNotificationTabChange("individual")}
                      >
                        Individual
                        <span className="notification-tab__count">
                          {unreadIndividualCount}
                        </span>
                      </button>
                    </div>
                    {notificationsLoading && (
                      <div className="p-3 text-center text-muted">Loading...</div>
                    )}
                    {!notificationsLoading && activeNotifications.length === 0 && (
                      <div className="p-3 text-center text-muted">
                        No new {notificationTab} notifications
                      </div>
                    )}
                    {!notificationsLoading &&
                      activeNotifications.map((note) => (
                        <div
                          key={note.notification_id}
                          className={`notification-item ${note.is_read ? 'is-read' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleNotificationOpen(note)}
                        >
                          <div className="notification-item__message">
                            {note.message}
                          </div>
                          <div className="notification-item__sender small text-muted">
                            {getSenderSummary(note).map((line) => (
                              <div key={line}>{line}</div>
                            ))}
                          </div>
                          <div className="notification-item__meta">
                            <span>{note.notification_type}</span>
                            <span>
                              {note.created_at
                                ? new Date(note.created_at).toLocaleString()
                                : ''}
                            </span>
                          </div>
                          {!note.is_read && (
                            <button
                              className="btn btn-link btn-sm p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkRead(note.notification_id);
                              }}
                            >
                              Mark read
                            </button>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* User Dropdown */}
             <div className="dropdown">
  <button
    className="action-btn user-avatar"
    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
  >
    {getUserInitial()}
  </button>

  {userDropdownOpen && (
    <div 
      className="dropdown-menu dropdown-menu-end show shadow border-0"
      style={{ 
        minWidth: '250px',
        right: 0,
        left: 'auto',
        transform: 'translateX(0)',
        marginTop: '15px'
      }}
    >
                    <div className="p-3 border-bottom">
                      <div className="fw-bold">{getUserName()}</div>
                      <small className="text-muted">{getRoleName()}</small>
                    </div>

                    <div className="py-2">
                      <Link
                        to="/profile"
                        className="dropdown-item d-flex align-items-center py-2 px-3"
                        onClick={() => setUserDropdownOpen(false)}
                      >
                        <FaUser className="me-3 text-muted" size={16} />
                        Profile
                      </Link>

                      <Link
                        to="/helpcenter"
                        className="dropdown-item d-flex align-items-center py-2 px-3"
                        onClick={() => setUserDropdownOpen(false)}
                      >
                        <FaQuestionCircle className="me-3 text-muted" size={16} />
                        Help Center
                      </Link>

                      <div className="dropdown-divider my-2"></div>

                      <button
                        className="dropdown-item d-flex align-items-center py-2 px-3 text-danger"
                        onClick={handleLogout}
                      >
                        <FaSignOutAlt className="me-3" size={16} />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        {/* Sidebar Header */}
        <div className="sidebar-header p-3 border-bottom bg-white d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
              <FaCalendarCheck className="text-white" size={20} />
            </div>
            <div>
              <div className="fw-bold text-primary">JapaKing</div>
              <div className="text-muted small">Learning Platform</div>
            </div>
          </div>
          <button className="sidebar-close-btn d-lg-none" onClick={closeSidebar}>
            <FaTimes />
          </button>
        </div>

        {/* Sidebar Menu Content */}
        <div className="sidebar-content">
          {loadingMenus ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : visibleMenus.length > 0 ? (
            visibleMenus.map((menu) => {
              const IconComponent = iconMap[menu.icon] || iconMap.default;
              const isActive = isActivePath(menu.ui_route);

              return (
                <div className="nav-item-custom" key={menu.menu_id}>
                  <Link
                    className={`nav-link-custom ${isActive ? 'active' : ''}`}
                    to={menu.ui_route}
                    onClick={closeSidebar}
                  >
                    <IconComponent className="nav-icon" size={18} />
                    <span className="nav-text">{menu.menu_name}</span>
                    {isActive && <FaChevronRight size={12} className="ms-auto" />}
                  </Link>
                </div>
              );
            })
          ) : (
            <div className="text-center py-5 text-muted">
              No menu items available
            </div>
          )}
        </div>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay d-lg-none" onClick={closeSidebar} />
      )}

      {/* Main Content Area */}
      <main className="main-content">
        {children}
      </main>
    </>
  );
};

export default NavBar;
